import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, MapPin, Target, Crosshair, Trash, Flag, Crown, NavigationArrow, Mountains } from "@phosphor-icons/react";
import { pushPendingShot, pendingShotCount } from "@/lib/pendingShots";
import { fetchElevations, elevationKey, playsLikeYards } from "@/lib/elevation";
import { fetchWind, shotBearing, headwindComponent, windAdjustedYards, describeWind } from "@/lib/wind";
import { fetchAirTemperatureF, airDensityAdjustedYards, describeAirDensity } from "@/lib/airDensity";
import { getCurrentPosition } from "@/lib/geo";

/**
 * Hole-by-hole satellite "flyover" for a course.
 *
 * Free Esri World Imagery satellite tiles + the green coordinates we already
 * seed. For each hole we draw the centreline (tee → green), distance rings
 * around the green, and a bag-based club suggestion ladder.
 *
 * Tee position priority:
 *   1. `hole.tee_lat` / `hole.tee_lng` — saved by an admin who walked the
 *      course and tapped "Save my GPS as tee" at the tee box.
 *   2. Back-projected estimate from the green-axis + total hole yardage.
 */

function yardsToDegLat(yards) { return (yards * 0.9144) / 111000; }
function yardsToDegLng(yards, lat) {
  return (yards * 0.9144) / (111000 * Math.cos((lat * Math.PI) / 180));
}

// Haversine yards (mirror of backend) — used for live rangefinder.
function haversineYards(la1, lo1, la2, lo2) {
  const R = 6371000;
  const toRad = (d) => d * Math.PI / 180;
  const dLat = toRad(la2 - la1);
  const dLng = toRad(lo2 - lo1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(la1)) * Math.cos(toRad(la2)) * Math.sin(dLng / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.sqrt(a)) * 1.0936133);
}

function estimateTeeLatLng(hole, totalYards) {
  if (hole.green_lat == null || hole.green_lng == null) return null;
  let dirLat = 1, dirLng = 0;
  if (hole.green_front_lat != null && hole.green_back_lat != null) {
    dirLat = hole.green_front_lat - hole.green_back_lat;
    dirLng = hole.green_front_lng - hole.green_back_lng;
  }
  const mag = Math.sqrt(dirLat * dirLat + dirLng * dirLng) || 1;
  const ux = dirLat / mag;
  const uy = dirLng / mag;
  const dy = yardsToDegLat(totalYards);
  const dx = yardsToDegLng(totalYards, hole.green_lat);
  return [hole.green_lat + ux * dy, hole.green_lng + uy * dx];
}

export default function CourseFlyover() {
  const { courseId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [hole, setHole] = useState(() => {
    // Honour ?hole= so deep-links from Score Entry land on the right hole.
    const q = Number(searchParams.get("hole"));
    return Number.isFinite(q) && q >= 1 && q <= 18 ? q : 1;
  });
  const [bag, setBag] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [savingTee, setSavingTee] = useState(false);
  // Admin "click-to-place" edit mode: null = off, "tee" or "green" when active.
  const [editMode, setEditMode] = useState(null);
  // --- Rangefinder state ---
  // target: { lat, lng } — defaults to mid-fairway between tee + green when the
  // hole loads. The player drags / taps it to point at any landing spot. The
  // big "yards to pin" readout + bag suggestion follow this point.
  const [target, setTarget] = useState(null);
  // playerPos: { lat, lng } — set when the player taps "Use my GPS" so the
  // rangefinder measures from their actual position instead of the tee.
  const [playerPos, setPlayerPos] = useState(null);
  const [locatingPlayer, setLocatingPlayer] = useState(false);
  const [trackingShot, setTrackingShot] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  // Plays-like state: { lat, lng, metres } for the player-position elevation
  // and the green centre elevation. Set when the toggle is on.
  const [playsLikeOn, setPlaysLikeOn] = useState(false);
  const [elevations, setElevations] = useState({}); // {key → metres}
  // Wind state — fetched alongside elevation when Plays Like is on.
  const [wind, setWind] = useState(null); // { speed_mph, direction_deg } | null
  // Air-density inputs — temperature + altitude (altitude comes from the
  // elevation API; temperature is its own /forecast call).
  const [tempF, setTempF] = useState(null);
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const editModeRef = useRef(null);
  // Refs for the live-overlay markers so we can move them without
  // tearing the whole map down on every drag.
  const targetMarkerRef = useRef(null);
  const playerMarkerRef = useRef(null);
  const targetLineRef = useRef(null);
  useEffect(() => { editModeRef.current = editMode; }, [editMode]);
  useEffect(() => { setPendingCount(pendingShotCount(courseId)); }, [courseId]);

  useEffect(() => {
    api.get(`/courses/${courseId}`).then((r) => setCourse(r.data)).catch(() => {});
    api.get("/users/me/bag").then((r) => setBag(r.data.clubs)).catch(() => {});
    api.get("/admin/whoami").then((r) => setIsAdmin(!!r.data.is_admin)).catch(() => {});
  }, [courseId]);

  const h = course?.holes.find((x) => x.number === hole);
  const tee = course?.tees.find((t) => t.name === "Blue") || course?.tees?.[0];
  const totalYards = tee?.yards?.[hole - 1] || 360;
  const green = h && h.green_lat != null ? [h.green_lat, h.green_lng] : null;
  // Use the admin-saved tee position if present, otherwise back-project.
  const savedTee = h && h.tee_lat != null && h.tee_lng != null ? [h.tee_lat, h.tee_lng] : null;
  const teePos = savedTee || (green ? estimateTeeLatLng(h, totalYards) : null);

  const saveTeeFromGPS = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported on this device"); return; }
    setSavingTee(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const r = await api.put(`/courses/${courseId}/holes/${hole}/tee`, {
            lat: latitude, lng: longitude,
          });
          // Patch local state so the marker snaps immediately.
          setCourse((c) => ({
            ...c,
            holes: c.holes.map((x) => x.number === hole
              ? { ...x, tee_lat: r.data.tee_lat, tee_lng: r.data.tee_lng }
              : x),
          }));
          toast.success(`Hole ${hole} tee saved (${pos.coords.accuracy?.toFixed(0) || "?"} m accuracy)`);
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
        } finally { setSavingTee(false); }
      },
      (err) => {
        setSavingTee(false);
        toast.error(`Couldn't get GPS — ${err.message}`);
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 },
    );
  };

  const clearTee = async () => {
    if (!window.confirm(`Clear the saved tee for hole ${hole}? The line will fall back to the estimate.`)) return;
    setSavingTee(true);
    try {
      await api.delete(`/courses/${courseId}/holes/${hole}/tee`);
      setCourse((c) => ({
        ...c,
        holes: c.holes.map((x) => x.number === hole
          ? { ...x, tee_lat: null, tee_lng: null }
          : x),
      }));
      toast.success(`Hole ${hole} tee cleared`);
    } catch (e) {
      toast.error("Clear failed");
    } finally { setSavingTee(false); }
  };

  const saveGreenFromGPS = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported on this device"); return; }
    setSavingTee(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const r = await api.put(`/courses/${courseId}/holes/${hole}/green`, {
            lat: latitude, lng: longitude,
          });
          setCourse((c) => ({
            ...c,
            holes: c.holes.map((x) => x.number === hole
              ? { ...x, green_lat: r.data.green_lat, green_lng: r.data.green_lng, green_mapped: true,
                  // Front/back will arrive on next full course fetch; nudge for now.
                  green_front_lat: r.data.green_lat, green_front_lng: r.data.green_lng,
                  green_back_lat: r.data.green_lat,  green_back_lng: r.data.green_lng }
              : x),
            mapping: c.mapping ? { ...c.mapping, greens: Math.min(18, (c.mapping.greens || 0) + (h?.green_mapped ? 0 : 1)) } : c.mapping,
          }));
          // Full refetch to capture the front/back coords + canonical mapping count.
          api.get(`/courses/${courseId}`).then((rr) => setCourse(rr.data));
          toast.success(`Hole ${hole} green saved (${pos.coords.accuracy?.toFixed(0) || "?"} m accuracy)`);
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
        } finally { setSavingTee(false); }
      },
      (err) => {
        setSavingTee(false);
        toast.error(`Couldn't get GPS — ${err.message}`);
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 },
    );
  };

  const clearGreenMapped = async () => {
    if (!window.confirm(`Unmark hole ${hole}'s green as hand-mapped? Coordinates stay but the counter drops.`)) return;
    setSavingTee(true);
    try {
      await api.delete(`/courses/${courseId}/holes/${hole}/green`);
      setCourse((c) => ({
        ...c,
        holes: c.holes.map((x) => x.number === hole ? { ...x, green_mapped: false } : x),
        mapping: c.mapping ? { ...c.mapping, greens: Math.max(0, (c.mapping.greens || 0) - 1) } : c.mapping,
      }));
      toast.success(`Hole ${hole} green unmapped`);
    } catch (e) {
      toast.error("Clear failed");
    } finally { setSavingTee(false); }
  };

  // Click-to-place: in edit mode, every click on the map saves the tapped
  // lat/lng as either the tee or the green for the current hole, then refetches.
  const handleMapClick = async (lat, lng) => {
    const mode = editModeRef.current;
    if (!mode) return;
    const curHole = holeRef.current;
    try {
      if (mode === "tee") {
        await api.put(`/courses/${courseId}/holes/${curHole}/tee`, { lat, lng });
        toast.success(`Hole ${curHole} tee placed`);
      } else {
        await api.put(`/courses/${courseId}/holes/${curHole}/green`, { lat, lng });
        toast.success(`Hole ${curHole} green placed`);
      }
      const fresh = await api.get(`/courses/${courseId}`);
      setCourse(fresh.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };
  // Keep a ref to the current hole number so the leaflet click handler — which
  // is registered once — always reads the live value.
  const holeRef = useRef(hole);
  useEffect(() => { holeRef.current = hole; }, [hole]);

  // Build / rebuild the Leaflet map whenever the hole or course changes.
  useEffect(() => {
    if (!mapEl.current || !green || !teePos) return;
    if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }

    const map = L.map(mapEl.current, {
      zoomControl: true, attributionControl: false, scrollWheelZoom: true,
      maxZoom: 19,
    });
    mapRef.current = map;
    // Click handler:
    //   - admin in tee/green edit mode → saves the position (existing behaviour)
    //   - everyone else → moves the rangefinder target to the clicked spot
    map.on("click", (e) => {
      if (editModeRef.current) {
        handleMapClick(e.latlng.lat, e.latlng.lng);
        return;
      }
      setTarget({ lat: e.latlng.lat, lng: e.latlng.lng });
    });
    // Esri World Imagery (satellite, free). Fiji coverage runs to ~zoom 17,
    // so we cap maxNativeZoom there and let Leaflet upscale tiles for tighter
    // hole views — looks better than the "Map data not yet available" tile.
    L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", {
      maxZoom: 19, maxNativeZoom: 17, crossOrigin: true,
    }).addTo(map);
    // Subtle OSM labels overlay so course/road names render on top of satellite.
    L.tileLayer("https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}.png", {
      maxZoom: 19, opacity: 0.85, subdomains: ["a", "b", "c", "d"], crossOrigin: true,
    }).addTo(map);

    // Centreline
    L.polyline([teePos, green], { color: "#FFC72C", weight: 3, opacity: 0.9, dashArray: "6 6" }).addTo(map);

    // Distance rings around green (100/150/200y)
    [100, 150, 200].forEach((y) => {
      L.circle(green, {
        radius: y * 0.9144,
        color: "white", weight: 1, opacity: 0.55, fillOpacity: 0, dashArray: "3 6",
      }).addTo(map).bindTooltip(`${y}y`, { permanent: false, direction: "center" });
    });

    // Tee marker
    const teeIcon = L.divIcon({
      className: "",
      html: `<div style="width:18px;height:18px;border-radius:50%;background:#0F2537;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,.4)"></div>`,
      iconSize: [18, 18], iconAnchor: [9, 9],
    });
    L.marker(teePos, { icon: teeIcon }).addTo(map)
      .bindTooltip("Tee", { permanent: true, direction: "top", offset: [0, -8] });

    // Green/pin marker
    const pinIcon = L.divIcon({
      className: "",
      html: `<div style="width:28px;height:28px;border-radius:50%;background:#E05D3A;display:flex;align-items:center;justify-content:center;color:white;box-shadow:0 4px 12px rgba(15,37,55,.45);border:2px solid white;font-weight:900;font-size:14px">⛳</div>`,
      iconSize: [28, 28], iconAnchor: [14, 14],
    });
    L.marker(green, { icon: pinIcon }).addTo(map)
      .bindTooltip(`${totalYards}y to pin`, { permanent: true, direction: "top", offset: [0, -16] });

    map.fitBounds([teePos, green], { padding: [40, 40], maxZoom: 17 });

    // Clear marker refs — they'll be recreated by the rangefinder effect below.
    targetMarkerRef.current = null;
    playerMarkerRef.current = null;
    targetLineRef.current = null;

    return () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
  }, [course, hole, green, teePos, totalYards]);

  // --- Rangefinder marker effect — runs whenever target/playerPos change. ---
  // We mutate the markers in place so dragging stays buttery; the parent map
  // effect above only rebuilds when the hole/course changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !green) return;
    // Target marker (draggable crosshair).
    if (target) {
      const html = `<div style="width:40px;height:40px;border-radius:9999px;border:3px solid white;background:rgba(224,93,58,.18);display:flex;align-items:center;justify-content:center;box-shadow:0 4px 14px rgba(15,37,55,.5)"><div style="width:8px;height:8px;border-radius:9999px;background:white"></div></div>`;
      const icon = L.divIcon({ className: "", html, iconSize: [40, 40], iconAnchor: [20, 20] });
      if (!targetMarkerRef.current) {
        const m = L.marker([target.lat, target.lng], { icon, draggable: true, autoPan: true, zIndexOffset: 1000 }).addTo(map);
        m.on("drag", (e) => {
          const ll = e.target.getLatLng();
          // Live-update line + readout — uses functional setState so we
          // don't tear leaflet's marker out from under itself.
          setTarget({ lat: ll.lat, lng: ll.lng });
        });
        targetMarkerRef.current = m;
      } else {
        targetMarkerRef.current.setLatLng([target.lat, target.lng]);
      }
    } else if (targetMarkerRef.current) {
      map.removeLayer(targetMarkerRef.current);
      targetMarkerRef.current = null;
    }

    // Player marker (only shown after "Use my GPS").
    if (playerPos) {
      const html = `<div style="width:18px;height:18px;border-radius:9999px;background:#3B82F6;border:3px solid white;box-shadow:0 0 0 6px rgba(59,130,246,.25)"></div>`;
      const icon = L.divIcon({ className: "", html, iconSize: [18, 18], iconAnchor: [9, 9] });
      if (!playerMarkerRef.current) {
        const m = L.marker([playerPos.lat, playerPos.lng], { icon, zIndexOffset: 900 }).addTo(map);
        m.bindTooltip("You", { permanent: false, direction: "top", offset: [0, -8] });
        playerMarkerRef.current = m;
      } else {
        playerMarkerRef.current.setLatLng([playerPos.lat, playerPos.lng]);
      }
    } else if (playerMarkerRef.current) {
      map.removeLayer(playerMarkerRef.current);
      playerMarkerRef.current = null;
    }

    // Line from origin (player GPS if set, else tee) → target → green.
    const origin = playerPos
      ? [playerPos.lat, playerPos.lng]
      : teePos;
    const linePoints = [];
    if (origin) linePoints.push(origin);
    if (target) linePoints.push([target.lat, target.lng]);
    linePoints.push(green);
    if (targetLineRef.current) { map.removeLayer(targetLineRef.current); targetLineRef.current = null; }
    if (linePoints.length >= 2 && target) {
      targetLineRef.current = L.polyline(linePoints, {
        color: "white", weight: 3, opacity: 0.95,
      }).addTo(map);
    }
  }, [target, playerPos, green, teePos]);

  // Seed the rangefinder target whenever the hole changes. Default to mid-fairway
  // (50% between tee and green) so the player sees a useful read immediately.
  useEffect(() => {
    if (!teePos || !green) { setTarget(null); return; }
    setTarget({
      lat: (teePos[0] + green[0]) / 2,
      lng: (teePos[1] + green[1]) / 2,
    });
    setPlayerPos(null);
  }, [hole, teePos?.[0], teePos?.[1], green?.[0], green?.[1]]);

  // Compute distances + bag suggestion + plays-like delta.
  const range = useMemo(() => {
    if (!green || !target) return null;
    const origin = playerPos ? [playerPos.lat, playerPos.lng] : (teePos || null);
    const toCentre = haversineYards(target.lat, target.lng, green[0], green[1]);
    let toFront = null, toBack = null;
    if (h?.green_front_lat != null && h?.green_back_lat != null) {
      toFront = haversineYards(target.lat, target.lng, h.green_front_lat, h.green_front_lng);
      toBack = haversineYards(target.lat, target.lng, h.green_back_lat, h.green_back_lng);
    }
    const carry = origin
      ? haversineYards(origin[0], origin[1], target.lat, target.lng)
      : null;
    return { toCentre, toFront, toBack, carry };
  }, [green, target, playerPos, teePos, h]);

  // Plays-like adjusted distances. We compute deltaMetres = greenElev - originElev
  // so positive = uphill = longer carry. Wind correction layers on top of
  // the elevation adjustment: +1.5%/mph headwind, –1%/mph tailwind (Tour rule).
  const playsLike = useMemo(() => {
    if (!playsLikeOn || !range || !target || !green) return null;
    const origin = playerPos ? [playerPos.lat, playerPos.lng] : (teePos || null);
    if (!origin) return null;
    const oKey = elevationKey(origin[0], origin[1]);
    const tKey = elevationKey(target.lat, target.lng);
    const gKey = elevationKey(green[0], green[1]);
    const oElev = elevations[oKey];
    const tElev = elevations[tKey];
    const gElev = elevations[gKey];
    if (oElev == null || tElev == null || gElev == null) return null;
    const elev_centre = playsLikeYards(range.toCentre, gElev - tElev);
    const elev_carry  = range.carry != null ? playsLikeYards(range.carry, tElev - oElev) : null;
    // Layer wind on top — uses bearing from origin → target (assumed shot line).
    let bearing = null, headwind_mph = 0;
    let wind_centre = elev_centre, wind_carry = elev_carry;
    if (wind) {
      bearing = shotBearing(origin[0], origin[1], target.lat, target.lng);
      headwind_mph = headwindComponent(wind, bearing);
      wind_centre = windAdjustedYards(elev_centre, headwind_mph);
      wind_carry  = elev_carry != null ? windAdjustedYards(elev_carry, headwind_mph) : null;
    }
    // Final layer — air density (temperature + altitude). gElev doubles as
    // the green's altitude in metres above sea level (Open-Meteo Elevation
    // returns metres ASL). For Fiji courses this is near zero; for Coorong /
    // Denver / Kabul-style mountain courses it can swing carry +10-20%.
    const ad_centre = airDensityAdjustedYards(wind_centre, tempF, gElev);
    const ad_carry  = wind_carry != null
      ? airDensityAdjustedYards(wind_carry, tempF, gElev)
      : null;
    return {
      toCentre: ad_centre,
      carry: ad_carry,
      delta_to_pin_m: Math.round((gElev - tElev) * 10) / 10,
      headwind_mph: Math.round(headwind_mph * 10) / 10,
      wind_label: wind && bearing != null ? describeWind(wind, bearing) : null,
      air_label: describeAirDensity(tempF, gElev),
    };
  }, [playsLikeOn, range, target, playerPos, teePos, green, elevations, wind, tempF]);

  // Fetch elevations + wind whenever toggle flips on (or target/player moves while on).
  useEffect(() => {
    if (!playsLikeOn || !target || !green) return;
    const origin = playerPos ? [playerPos.lat, playerPos.lng] : (teePos || null);
    if (!origin) return;
    const points = [
      { lat: origin[0], lng: origin[1] },
      { lat: target.lat, lng: target.lng },
      { lat: green[0], lng: green[1] },
    ];
    let cancelled = false;
    fetchElevations(points).then((map) => {
      if (cancelled) return;
      setElevations((prev) => {
        const out = { ...prev };
        map.forEach((v, k) => { out[k] = v; });
        return out;
      });
    });
    // Wind only needs one sample point — use the green centre as the canonical
    // location, since the hole is small enough for surface wind to be uniform.
    fetchWind(green[0], green[1]).then((w) => {
      if (cancelled) return;
      setWind(w);
    });
    // Air temperature — same single-point assumption.
    fetchAirTemperatureF(green[0], green[1]).then((t) => {
      if (cancelled) return;
      setTempF(t);
    });
    return () => { cancelled = true; };
  }, [playsLikeOn, target?.lat, target?.lng, playerPos?.lat, playerPos?.lng, green?.[0], green?.[1], teePos?.[0], teePos?.[1]]);

  // Suggested club from the user's bag — matches the carry distance to the
  // target (or distance to centre when no GPS is set). Picks the shortest club
  // whose `distance_yards` ≥ target. Falls back to the longest club.
  const suggestedClub = useMemo(() => {
    if (!bag?.length || !range) return null;
    const target_yds = playsLike?.carry ?? range.carry ?? playsLike?.toCentre ?? range.toCentre;
    const sorted = bag.filter((c) => c.distance_yards > 0).slice().sort((a, b) => a.distance_yards - b.distance_yards);
    if (!sorted.length) return null;
    const pick = sorted.find((c) => c.distance_yards >= target_yds);
    return pick || sorted[sorted.length - 1];
  }, [bag, range, playsLike]);

  const useMyGPS = () => {
    setLocatingPlayer(true);
    getCurrentPosition({ highAccuracy: true, timeoutMs: 10000 })
      .then((pos) => {
        setPlayerPos({ lat: pos.latitude, lng: pos.longitude });
        toast.success(`Locked to your position (±${pos.accuracy?.toFixed(0) || "?"} m)`);
      })
      .catch((err) => toast.error(`GPS failed — ${err.message || err}`))
      .finally(() => setLocatingPlayer(false));
  };

  const trackShotHere = () => {
    if (!target) return;
    if (!user?.is_pro) {
      toast.error("Bula Pro feature — Go Pro to enable shot tracking");
      return;
    }
    setTrackingShot(true);
    try {
      pushPendingShot({
        courseId,
        hole,
        lat: target.lat,
        lng: target.lng,
        club: suggestedClub?.club || "Driver",
      });
      setPendingCount(pendingShotCount(courseId));
      toast.success(`Shot saved — will sync to your next round at ${course?.name?.split(" ")[0] || "this course"}`);
    } finally { setTrackingShot(false); }
  };

  // Bag suggestions for the remaining yardage from the tee.
  const suggestions = useMemo(() => {
    if (!bag.length || !totalYards) return [];
    return bag
      .filter((c) => c.distance_yards > 0)
      .slice()
      .sort((a, b) => b.distance_yards - a.distance_yards);
  }, [bag, totalYards]);

  if (!course) return <div className="py-10">Loading…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Link to={`/courses/${courseId}`} data-testid="back-to-course" className="inline-flex items-center gap-1 text-sm font-bold text-[#0F2537]/70 hover:text-[#0F2537]">
          <ArrowLeft size={14} weight="bold" /> {course.name}
        </Link>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <>
              <button onClick={() => setEditMode((m) => m === "tee" ? null : "tee")} data-testid="edit-tee-mode-btn"
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold border-2 transition ${
                  editMode === "tee" ? "bg-[#E05D3A] text-white border-[#E05D3A]" : "bg-white text-[#E05D3A] border-[#E05D3A]/40 hover:border-[#E05D3A]"
                }`}
                title="Click anywhere on the map to drop the tee for this hole">
                <Crosshair size={11} weight="bold" /> {editMode === "tee" ? `Click map → Tee H${hole}` : `Place tee H${hole}`}
              </button>
              <button onClick={() => setEditMode((m) => m === "green" ? null : "green")} data-testid="edit-green-mode-btn"
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold border-2 transition ${
                  editMode === "green" ? "bg-[#1E5631] text-white border-[#1E5631]" : "bg-white text-[#1E5631] border-[#1E5631]/40 hover:border-[#1E5631]"
                }`}
                title="Click anywhere on the map to drop the green centre for this hole">
                <Flag size={11} weight="bold" /> {editMode === "green" ? `Click map → Green H${hole}` : `Place green H${hole}`}
              </button>
              <button onClick={saveTeeFromGPS} disabled={savingTee} data-testid="save-tee-gps-btn"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#0F2537] text-white text-[11px] font-bold hover:brightness-110 disabled:opacity-60"
                title="Stand at the tee box and tap — saves your current GPS as the tee for this hole">
                <Crosshair size={11} weight="bold" /> {savingTee ? "…" : "GPS tee"}
              </button>
              <button onClick={saveGreenFromGPS} disabled={savingTee} data-testid="save-green-gps-btn"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#0F2537] text-white text-[11px] font-bold hover:brightness-110 disabled:opacity-60"
                title="Stand on the green centre and tap — saves your current GPS as the pin for this hole">
                <Flag size={11} weight="bold" /> {savingTee ? "…" : "GPS green"}
              </button>
              {savedTee && (
                <button onClick={clearTee} disabled={savingTee} data-testid="clear-tee-btn"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-[#E2E0D8] text-[#0F2537]/60 text-[11px] font-bold hover:text-red-600"
                  title="Revert this hole's tee to the auto-estimate">
                  <Trash size={11} weight="bold" />
                </button>
              )}
              {h?.green_mapped && (
                <button onClick={clearGreenMapped} disabled={savingTee} data-testid="clear-green-btn"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-[#E2E0D8] text-[#0F2537]/60 text-[11px] font-bold hover:text-red-600"
                  title="Unmark this hole's green as hand-mapped">
                  <Trash size={11} weight="bold" />
                </button>
              )}
            </>
          )}
          <div className="text-xs label-eyebrow flex items-center gap-1" data-testid={savedTee ? "tee-status-saved" : "tee-status-estimate"}>
            <MapPin size={12} weight="fill" /> Tee · {savedTee ? "saved" : "estimate"}
          </div>
          <div className="text-xs label-eyebrow flex items-center gap-1" data-testid={h?.green_mapped ? "green-status-saved" : "green-status-estimate"}>
            <Flag size={12} weight="fill" /> Green · {h?.green_mapped ? "saved" : "estimate"}
          </div>
        </div>
      </div>

      <div className="card-default overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E2E0D8] bg-[#0F2537] text-white">
          <button onClick={() => setHole((n) => Math.max(1, n - 1))} disabled={hole === 1} data-testid="flyover-prev"
            className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-30 text-sm font-bold inline-flex items-center gap-1">
            <ArrowLeft size={14} weight="bold" /> Prev
          </button>
          <div className="text-center">
            <div className="label-eyebrow text-white/60">Hole {hole}</div>
            <div className="font-heading font-black text-lg leading-none mt-0.5">
              Par {h?.par} · {totalYards} yds {h?.handicap ? `· SI ${h.handicap}` : ""}
            </div>
          </div>
          <button onClick={() => setHole((n) => Math.min(18, n + 1))} disabled={hole === 18} data-testid="flyover-next"
            className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-30 text-sm font-bold inline-flex items-center gap-1">
            Next <ArrowRight size={14} weight="bold" />
          </button>
        </div>

        <div className="relative" style={{ height: "60vh", minHeight: 380 }} data-testid="flyover-map-wrap">
          {green && teePos ? (
            <div ref={mapEl} className={`w-full h-full ${editMode ? "cursor-crosshair" : ""}`} data-testid="flyover-map" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-[#0F2537]/60">
              No GPS data available for this hole.
            </div>
          )}
          {editMode && (
            <div className="absolute top-3 left-3 z-[1000] bg-[#0F2537]/90 text-white text-xs font-bold px-3 py-1.5 rounded-lg shadow-lg pointer-events-none flex items-center gap-1.5" data-testid="edit-mode-banner">
              {editMode === "tee" ? <Crosshair size={12} weight="bold" /> : <Flag size={12} weight="bold" />}
              Click anywhere on the map to place {editMode === "tee" ? "the TEE" : "the GREEN"} for hole {hole}
            </div>
          )}

          {/* Rangefinder overlay — only renders when not in admin edit mode */}
          {!editMode && range && (
            <div className="absolute top-3 left-3 z-[1000] flex flex-col gap-2 pointer-events-none max-w-[260px]" data-testid="rangefinder-overlay">
              {/* Big "yards to pin" tile */}
              <div className="bg-[#0F2537]/85 backdrop-blur-sm text-white px-3 py-2 rounded-xl shadow-lg pointer-events-auto">
                <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold">To pin</div>
                <div className="flex items-end gap-1.5">
                  <div className="font-heading font-black text-3xl leading-none tabular-nums" data-testid="rf-centre">
                    {playsLike?.toCentre ?? range.toCentre}
                  </div>
                  <div className="text-xs font-bold text-white/70 mb-0.5">yd{playsLikeOn && playsLike ? (playsLike.delta_to_pin_m > 0 ? " ↑" : playsLike.delta_to_pin_m < 0 ? " ↓" : "") : ""}</div>
                </div>
                {range.toFront != null && range.toBack != null && (
                  <div className="mt-1 text-[10px] font-bold text-white/80 tabular-nums flex gap-2.5" data-testid="rf-fcb">
                    <span data-testid="rf-front">F {range.toFront}</span>
                    <span className="text-white/30">·</span>
                    <span data-testid="rf-back">B {range.toBack}</span>
                  </div>
                )}
                {suggestedClub && (
                  <div className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold bg-[#E05D3A] text-white px-2 py-0.5 rounded-md" data-testid="rf-club-suggestion">
                    <Target size={10} weight="fill" />
                    {suggestedClub.club} <span className="opacity-80 font-normal">({suggestedClub.distance_yards}y)</span>
                  </div>
                )}
                {range.carry != null && (
                  <div className="mt-1 text-[10px] font-bold text-white/65" data-testid="rf-carry">
                    Carry to target: {playsLike?.carry ?? range.carry} yd
                  </div>
                )}
                {playsLikeOn && playsLike?.wind_label && (
                  <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-white bg-white/10 px-1.5 py-0.5 rounded" data-testid="rf-wind">
                    <span aria-hidden="true">🌬</span> {playsLike.wind_label}
                  </div>
                )}
                {playsLikeOn && playsLike?.air_label && (
                  <div className="mt-1 ml-1 inline-flex items-center gap-1 text-[10px] font-bold text-white bg-white/10 px-1.5 py-0.5 rounded" data-testid="rf-air-density">
                    <span aria-hidden="true">🌡</span> {playsLike.air_label}
                  </div>
                )}
                {playsLikeOn && !playsLike && (
                  <div className="mt-1 text-[10px] font-bold text-[#FFC72C]">Fetching elevation + wind…</div>
                )}
              </div>

              {/* Toolbar — buttons stack under the readout */}
              <div className="flex flex-wrap gap-1.5 pointer-events-auto" data-testid="rf-toolbar">
                <button onClick={useMyGPS} disabled={locatingPlayer} data-testid="rf-use-gps"
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/95 backdrop-blur-sm text-[#0F2537] text-[11px] font-bold shadow-md hover:bg-white disabled:opacity-60 transition">
                  <NavigationArrow size={11} weight="fill" /> {locatingPlayer ? "Locating…" : (playerPos ? "Re-lock GPS" : "Use my GPS")}
                </button>
                <button onClick={() => setPlaysLikeOn((v) => !v)} data-testid="rf-plays-like-toggle"
                  className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-bold shadow-md transition ${
                    playsLikeOn ? "bg-[#FFC72C] text-[#0F2537]" : "bg-white/95 backdrop-blur-sm text-[#0F2537] hover:bg-white"
                  }`}
                  title="Account for elevation difference between you and the pin">
                  <Mountains size={11} weight="fill" /> Plays Like {playsLikeOn ? "ON" : "OFF"}
                </button>
                <button onClick={trackShotHere} disabled={trackingShot || !user?.is_pro} data-testid="rf-track-shot"
                  className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-bold shadow-md transition ${
                    user?.is_pro ? "bg-[#E05D3A] text-white hover:brightness-110" : "bg-white/70 text-[#0F2537]/50 cursor-not-allowed"
                  }`}
                  title={user?.is_pro ? "Save this target as a tracked shot — syncs to your next round" : "Bula Pro feature"}>
                  {!user?.is_pro && <Crown size={11} weight="fill" />}
                  <Target size={11} weight="fill" /> Track shot
                </button>
              </div>

              {/* Pending-shots badge — small reassurance that the offline shots are queued */}
              {pendingCount > 0 && (
                <div className="inline-flex items-center gap-1 self-start px-2 py-1 rounded-full bg-[#1E5631] text-white text-[10px] font-bold shadow-md pointer-events-auto" data-testid="rf-pending-count">
                  ⚡ {pendingCount} shot{pendingCount === 1 ? "" : "s"} ready to sync
                </div>
              )}
            </div>
          )}
        </div>

        {suggestions.length > 0 && (
          <div className="px-5 py-3 border-t border-[#E2E0D8]" data-testid="flyover-bag-suggest">
            <div className="label-eyebrow mb-1">From the tee — {totalYards}y to pin</div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {suggestions.map((c) => {
                const remaining = totalYards - c.distance_yards;
                const recommended = Math.abs(remaining) < 15;
                return (
                  <div key={c.club} className={`shrink-0 rounded-lg px-3 py-2 text-xs border ${recommended ? "border-[#E05D3A] bg-[#E05D3A]/5" : "border-[#E2E0D8]"}`}>
                    <div className="font-bold flex items-center gap-1">{c.club} {recommended && <Target size={11} weight="fill" className="text-[#E05D3A]" />}</div>
                    <div className="text-[10px] text-[#0F2537]/55">{c.distance_yards}y · leaves {remaining > 0 ? `${remaining}y` : "over"}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-9 gap-1 text-center" data-testid="flyover-jump-row">
        {course.holes.map((x) => (
          <button key={x.number} onClick={() => setHole(x.number)} data-testid={`flyover-jump-${x.number}`}
            className={`py-2 rounded-lg text-xs font-bold ${x.number === hole ? "bg-[#0F2537] text-white" : "bg-white border border-[#E2E0D8] text-[#0F2537] hover:border-[#0F2537]"}`}>
            <div className="text-[9px] opacity-60">H</div>
            <div className="leading-none">{x.number}</div>
          </button>
        ))}
      </div>
    </div>
  );
}
